import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import {
  AppAlert,
  AppBadge,
  AppButton,
  AppConfirmModal,
  AppFormField,
  AppInput,
  AppModal,
  AppSpinner,
} from '../ui';
import { notify } from '../common/AppToast';
import roleService from '../../services/roleService';
import { getRoleBadgeVariant } from '../../config/userRoles';
import { ROUTES } from '../../config/routes';
import '../../styles/users-admin.scss';

const UserRolesManageModal = ({
  show,
  roles = [],
  loading = false,
  onClose,
  onChanged,
}) => {
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (!show) {
      setLabel('');
      setError(null);
      setEditingId(null);
      setDeleteTarget(null);
    }
  }, [show]);

  const handleCreate = async (event) => {
    event.preventDefault();
    const name = label.trim();
    if (name.length < 2) {
      setError('Indiquez un nom de rôle (2 caractères minimum).');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const created = await roleService.create({ label: name });
      setLabel('');
      notify.success(`Rôle « ${created.label} » créé`);
      await onChanged?.(created);
    } catch (err) {
      setError(err.message || 'Impossible de créer le rôle');
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (role) => {
    const name = editingLabel.trim();
    if (name.length < 2) {
      setError('Indiquez un nom de rôle (2 caractères minimum).');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await roleService.update(role.id, { label: name });
      setEditingId(null);
      notify.success('Rôle renommé');
      await onChanged?.();
    } catch (err) {
      setError(err.message || 'Impossible de renommer le rôle');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setSaving(true);
    setError(null);
    try {
      await roleService.remove(deleteTarget.id);
      notify.success(`Rôle « ${deleteTarget.label} » supprimé`);
      setDeleteTarget(null);
      await onChanged?.();
    } catch (err) {
      setError(err.message || 'Impossible de supprimer le rôle');
    } finally {
      setSaving(false);
    }
  };

  const customRoles = roles.filter((role) => !role.system);
  const systemRoles = roles.filter((role) => role.system);

  return (
    <>
      <AppModal
        show={show}
        title="Rôles"
        size="lg"
        scrollable
        onClose={onClose}
        footer={(
          <AppButton variant="outline-secondary" onClick={onClose}>
            Fermer
          </AppButton>
        )}
      >
        <p className="text-secondary-light text-sm mb-3">
          Les nouveaux rôles peuvent être attribués aux utilisateurs. Ensuite, ouvrez{' '}
          <Link to={ROUTES.admin.menuVisibility}>Droits des pages</Link>
          {' '}pour choisir les menus visibles.
        </p>

        <form className="row g-2 align-items-end mb-4" onSubmit={handleCreate}>
          <AppFormField className="col" label="Nouveau rôle" htmlFor="new-role-label" required>
            <AppInput
              id="new-role-label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Ex. Comptable"
              disabled={saving}
            />
          </AppFormField>
          <div className="col-auto">
            <AppButton type="submit" icon="mdi:plus" loading={saving} loadingLabel="Création...">
              Créer
            </AppButton>
          </div>
        </form>

        {error ? <AppAlert className="mb-3">{error}</AppAlert> : null}

        {loading ? (
          <AppSpinner />
        ) : (
          <>
            {customRoles.length > 0 ? (
              <div className="mb-4">
                <h6 className="fw-semibold mb-2">Rôles personnalisés</h6>
                <ul className="list-unstyled mb-0 user-role-manage-list">
                  {customRoles.map((role) => (
                    <li key={role.value} className="user-role-manage-list__item">
                      {editingId === role.id ? (
                        <div className="d-flex gap-2 align-items-center flex-grow-1">
                          <AppInput
                            value={editingLabel}
                            onChange={(event) => setEditingLabel(event.target.value)}
                            disabled={saving}
                          />
                          <AppButton size="sm" onClick={() => handleRename(role)} disabled={saving}>
                            Enregistrer
                          </AppButton>
                          <AppButton
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => setEditingId(null)}
                            disabled={saving}
                          >
                            Annuler
                          </AppButton>
                        </div>
                      ) : (
                        <>
                          <AppBadge variant={getRoleBadgeVariant(role.value)} pill>
                            {role.label}
                          </AppBadge>
                          <code className="text-xs text-secondary-light">{role.value}</code>
                          <div className="ms-auto d-flex gap-1">
                            <AppButton
                              size="sm"
                              variant="outline-primary"
                              icon="mdi:pencil-outline"
                              title="Renommer"
                              onClick={() => {
                                setEditingId(role.id);
                                setEditingLabel(role.label);
                              }}
                            />
                            <AppButton
                              size="sm"
                              variant="outline-danger"
                              icon="mdi:trash-can-outline"
                              title="Supprimer"
                              onClick={() => setDeleteTarget(role)}
                            />
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-secondary-light text-sm">Aucun rôle personnalisé pour le moment.</p>
            )}

            <h6 className="fw-semibold mb-2">Rôles système</h6>
            <div className="d-flex flex-wrap gap-1">
              {systemRoles.map((role) => (
                <AppBadge key={role.value} variant={getRoleBadgeVariant(role.value)} pill>
                  <Icon icon="mdi:lock-outline" width={12} aria-hidden />
                  {role.label}
                </AppBadge>
              ))}
            </div>
          </>
        )}
      </AppModal>

      <AppConfirmModal
        show={Boolean(deleteTarget)}
        title="Supprimer ce rôle ?"
        confirmLabel="Supprimer"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      >
        {deleteTarget ? (
          <p className="mb-0">
            Le rôle <strong>{deleteTarget.label}</strong> sera retiré des utilisateurs et des skills.
            Les droits de pages associés seront également supprimés.
          </p>
        ) : null}
      </AppConfirmModal>
    </>
  );
};

export default UserRolesManageModal;
